import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getChildren, linkChild, unlinkChild, getChildProgress, cancelLinkRequest, ChildProgress, LinkRequestForParent } from '../services/auth';
import { SetGoalsModal } from '../components/parent/SetGoalsModal';
import { GoalProgressCard } from '../components/parent/GoalProgressCard';
import { WeeklyReportPanel } from '../components/parent/WeeklyReportPanel';
import { ParentKnowledgeGaps } from '../components/parent/ParentKnowledgeGaps';
import { ParentRecommendations } from '../components/parent/ParentRecommendations';
import { CreateAssignmentModal } from '../components/parent/CreateAssignmentModal';
import { AssignmentsList } from '../components/parent/AssignmentsList';
import { EmailPreferences } from '../components/parent/EmailPreferences';
import { AIInsightsPanel } from '../components/parent/AIInsightsPanel';
import { GateBypassToggle } from '../components/parent/GateBypassToggle';
import { storage } from '../services/storage';
import '../styles/ParentDashboard.css';

interface ChildDetails {
  student: {
    id: string;
    name: string;
    email: string;
  };
  stats: {
    total_questions: number;
    correct_answers: number;
    accuracy: number;
    days_practiced: number;
    first_activity: string | null;
    last_activity: string | null;
  };
  unit_progress: Array<{
    unit: number;
    total_los: number;
    mastered_los: number;
    avg_mastery: number;
  }>;
  recent_activity: Array<{
    date: string;
    questions: number;
    correct: number;
  }>;
}

type TabKey = 'overview' | 'weekly' | 'gaps' | 'recommendations' | 'assignments' | 'ai-insights' | 'settings';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Group children by email to handle multi-grade
interface GroupedChild {
  email: string;
  name: string;
  curricula: ChildProgress[];
}

function groupChildrenByEmail(children: ChildProgress[]): GroupedChild[] {
  const groups: Record<string, GroupedChild> = {};
  for (const child of children) {
    const key = child.email || child.student_id;
    if (!groups[key]) {
      groups[key] = {
        email: child.email || '',
        name: child.name,
        curricula: [],
      };
    }
    groups[key].curricula.push(child);
  }
  return Object.values(groups);
}

export const ParentDashboardPage: React.FC = () => {
  const { user, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState<ChildProgress[]>([]);
  const [linkRequests, setLinkRequests] = useState<LinkRequestForParent[]>([]);
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState<string | null>(null);
  const [childDetails, setChildDetails] = useState<ChildDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentRefresh, setAssignmentRefresh] = useState(0);
  const [showParentWelcome, setShowParentWelcome] = useState(!storage.hasSeenTip('parent_welcome'));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Group children by email
  const groupedChildren = groupChildrenByEmail(children);

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadChildDetails(selectedChild);
      setActiveTab('overview');
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const response = await getChildren();
      setChildren(response.children);
      setLinkRequests(response.link_requests || []);
      if (response.children.length > 0 && !selectedChild) {
        const firstChild = response.children[0];
        setSelectedChild(firstChild.student_id);
        setSelectedCurriculum(firstChild.curriculum || null);
      }
    } catch (err) {
      console.error('Failed to load children:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildDetails = async (studentId: string) => {
    setIsLoadingDetails(true);
    try {
      const details = await getChildProgress(studentId);
      setChildDetails(details);
    } catch (err) {
      console.error('Failed to load child details:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleLinkChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkEmail) return;

    setIsLinking(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await linkChild(linkEmail);
      setSuccessMessage(result.message || 'Link request sent!');
      setLinkEmail('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to link child account');
    } finally {
      setIsLinking(false);
      // Always refresh children list — the link may have succeeded
      // even if the response errored (network issue, timeout, etc.)
      await loadChildren();
    }
  };

  const handleUnlinkChild = async (studentId: string) => {
    if (!window.confirm('Are you sure you want to unlink this child?')) return;

    try {
      await unlinkChild(studentId);
      setChildren(children.filter(c => c.student_id !== studentId));
      if (selectedChild === studentId) {
        setSelectedChild(children.length > 1 ? children[0].student_id : null);
        setChildDetails(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unlink child');
    }
  };

  const handleCancelRequest = async (studentId: string) => {
    try {
      await cancelLinkRequest(studentId);
      await loadChildren();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const selectedChildName = childDetails?.student.name || children.find(c => c.student_id === selectedChild)?.name || '';

  // Separate link requests by status (exclude accepted — those are in children list)
  const pendingRequests = linkRequests.filter(r => r.status === 'pending');
  const declinedRequests = linkRequests.filter(r => r.status === 'declined');

  if (isLoading) {
    return (
      <div className="parent-dashboard loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'weekly', label: 'Weekly Report' },
    { key: 'gaps', label: 'Knowledge Gaps' },
    { key: 'recommendations', label: 'Recommendations' },
    { key: 'assignments', label: 'Assignments' },
    { key: 'ai-insights', label: 'AI Insights' },
    { key: 'settings', label: 'Settings' },
  ];

  const renderTabContent = () => {
    if (!selectedChild) return null;

    switch (activeTab) {
      case 'overview':
        return (
          <>
            {/* Goal Progress + Set Goals */}
            <div className="goals-section">
              <div className="goals-header">
                <h3>Daily Goals</h3>
                <button
                  className="set-goals-btn"
                  onClick={() => setShowGoalsModal(true)}
                >
                  Set Goals
                </button>
              </div>
              <GoalProgressCard studentId={selectedChild} />
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <h3>Questions Answered</h3>
                <div className="stat-value">{childDetails?.stats.total_questions}</div>
              </div>
              <div className="stat-card">
                <h3>Accuracy</h3>
                <div className="stat-value">{childDetails?.stats.accuracy}%</div>
              </div>
              <div className="stat-card">
                <h3>Days Practiced</h3>
                <div className="stat-value">{childDetails?.stats.days_practiced}</div>
              </div>
              <div className="stat-card">
                <h3>Last Active</h3>
                <div className="stat-value">
                  {childDetails?.stats.last_activity
                    ? new Date(childDetails.stats.last_activity).toLocaleDateString()
                    : 'Never'}
                </div>
              </div>
            </div>

            <div className="unit-progress-section">
              <h3>Unit Progress</h3>
              <div className="unit-progress-list">
                {childDetails?.unit_progress.map((unit) => (
                  <div key={unit.unit} className="unit-progress-item">
                    <div className="unit-info">
                      <span className="unit-name">Unit {unit.unit}</span>
                      <span className="unit-stats">
                        {unit.mastered_los}/{unit.total_los} objectives mastered
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${unit.avg_mastery}%` }}
                      />
                    </div>
                    <span className="progress-percent">{unit.avg_mastery}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="recent-activity-section">
              <h3>Recent Activity</h3>
              {childDetails?.recent_activity.length === 0 ? (
                <p>No recent activity</p>
              ) : (
                <table className="activity-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Questions</th>
                      <th>Correct</th>
                      <th>Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {childDetails?.recent_activity.map((day) => (
                      <tr key={day.date}>
                        <td>{new Date(day.date).toLocaleDateString()}</td>
                        <td>{day.questions}</td>
                        <td>{day.correct}</td>
                        <td>{Math.round((day.correct / day.questions) * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        );

      case 'weekly':
        return <WeeklyReportPanel studentId={selectedChild} />;

      case 'gaps':
        return <ParentKnowledgeGaps studentId={selectedChild} />;

      case 'recommendations':
        return <ParentRecommendations studentId={selectedChild} />;

      case 'assignments':
        return (
          <div className="assignments-section">
            <div className="assignments-header">
              <h3>Assignments</h3>
              <button
                className="create-assignment-btn"
                onClick={() => setShowAssignmentModal(true)}
              >
                + New Assignment
              </button>
            </div>
            <AssignmentsList
              studentId={selectedChild}
              refreshTrigger={assignmentRefresh}
            />
          </div>
        );

      case 'ai-insights':
        return <AIInsightsPanel studentId={selectedChild} />;

      case 'settings':
        return (
          <div className="settings-section">
            <h3>Unit Access</h3>
            <GateBypassToggle
              studentId={selectedChild}
              childName={selectedChildName}
            />

            <h3>Email Settings</h3>
            <EmailPreferences />

            <h3 style={{ color: '#dc2626' }}>Danger Zone</h3>
            {!showDeleteConfirm ? (
              <button
                className="delete-account-btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Account
              </button>
            ) : (
              <div className="delete-confirm-box">
                <p>This will permanently delete your account and unlink all children. This cannot be undone.</p>
                <p>Type <strong>DELETE</strong> to confirm:</p>
                <input
                  type="text"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder="Type DELETE"
                  disabled={isDeleting}
                />
                <div className="delete-confirm-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-delete-btn"
                    onClick={async () => {
                      setIsDeleting(true);
                      try {
                        await deleteAccount();
                        navigate('/login', { replace: true });
                      } catch {
                        setIsDeleting(false);
                      }
                    }}
                    disabled={deleteText !== 'DELETE' || isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="parent-dashboard">
      <div className="dashboard-header">
        <h1>Parent Dashboard</h1>
        <p>{storage.hasSeenTip('parent_welcome') ? 'Welcome back' : 'Welcome'}, {user?.name}! Monitor your children's learning progress.</p>
      </div>

      {showParentWelcome && (
        <div className="parent-welcome-card">
          <button
            className="welcome-close"
            onClick={() => {
              storage.markTipSeen('parent_welcome');
              setShowParentWelcome(false);
            }}
          >
            &times;
          </button>
          <h3>Welcome to the Parent Dashboard</h3>
          <p>Here's what you can do:</p>
          <ul className="welcome-features">
            <li>
              <strong>Link your child</strong> &mdash; Enter their email in the sidebar to request access to their progress.
            </li>
            <li>
              <strong>Overview</strong> &mdash; See daily goals, stats, and unit progress at a glance.
            </li>
            <li>
              <strong>Knowledge Gaps</strong> &mdash; Discover which topics your child is struggling with, so you can offer targeted help.
            </li>
            <li>
              <strong>AI Insights</strong> &mdash; See how your child uses the AI tutor and whether it's helping.
            </li>
            <li>
              <strong>Set Goals</strong> &mdash; Set daily practice goals and track completion.
            </li>
          </ul>
        </div>
      )}

      <div className="dashboard-content">
        {/* Sidebar with children list */}
        <aside className="children-sidebar">
          <h2>Your Children</h2>

          {groupedChildren.length === 0 ? (
            <p className="no-children">No children linked yet.</p>
          ) : (
            <ul className="children-list">
              {groupedChildren.map((group) => {
                const isSelected = group.curricula.some(c => c.student_id === selectedChild);
                const currentChild = group.curricula.find(c => c.student_id === selectedChild) || group.curricula[0];
                const hasMultipleCurricula = group.curricula.length > 1;

                return (
                  <li
                    key={group.email || group.curricula[0].student_id}
                    className={`child-item ${isSelected ? 'active' : ''}`}
                    onClick={() => {
                      if (!isSelected) {
                        setSelectedChild(group.curricula[0].student_id);
                        setSelectedCurriculum(group.curricula[0].curriculum || null);
                      }
                    }}
                  >
                    <div className="child-info">
                      <span className="child-name">{group.name}</span>
                      {hasMultipleCurricula && isSelected ? (
                        <select
                          className="curriculum-select"
                          value={selectedChild || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            const newStudentId = e.target.value;
                            const curriculum = group.curricula.find(c => c.student_id === newStudentId);
                            setSelectedChild(newStudentId);
                            setSelectedCurriculum(curriculum?.curriculum || null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {group.curricula.map(c => (
                            <option key={c.student_id} value={c.student_id}>
                              {c.curriculum_display_name || c.curriculum}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="child-progress">
                          {currentChild.curriculum_display_name || currentChild.curriculum || 'Linked'}
                        </span>
                      )}
                    </div>
                    <button
                      className="unlink-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnlinkChild(currentChild.student_id);
                      }}
                      title="Unlink child"
                    >
                      &times;
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {pendingRequests.length > 0 && (
            <div className="pending-requests-section">
              {pendingRequests.map((req) => (
                <div key={req.id} className="child-item pending">
                  <div className="child-info">
                    <span className="child-name">
                      {req.child_name}
                      {req.child_curriculum_display_name && (
                        <span className="child-curriculum-badge">
                          {req.child_curriculum_display_name}
                        </span>
                      )}
                    </span>
                    <span className="child-status-text pending-text">
                      Waiting for approval
                      {req.requested_at && (
                        <> &middot; Requested {timeAgo(req.requested_at)}</>
                      )}
                    </span>
                    <span className="child-status-hint">
                      They'll see your request when they log in to this grade.
                    </span>
                  </div>
                  <button
                    className="unlink-button"
                    onClick={() => handleCancelRequest(req.child_student_id)}
                    title="Cancel request"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {declinedRequests.length > 0 && (
            <div className="declined-requests-section">
              {declinedRequests.map((req) => {
                const canRetry = req.responded_at
                  ? new Date().getTime() - new Date(req.responded_at).getTime() > 7 * 24 * 60 * 60 * 1000
                  : true;
                const retryDate = req.responded_at
                  ? new Date(new Date(req.responded_at).getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : '';
                return (
                  <div key={req.id} className="child-item declined">
                    <div className="child-info">
                      <span className="child-name">
                        {req.child_name}
                        {req.child_curriculum_display_name && (
                          <span className="child-curriculum-badge">
                            {req.child_curriculum_display_name}
                          </span>
                        )}
                      </span>
                      <span className="child-status-text declined-text">
                        {canRetry
                          ? 'Request declined. You can try again.'
                          : `Request declined \u00b7 You can try again after ${retryDate}`}
                      </span>
                      <span className="child-status-hint">
                        Talk to your child about why linking can help them &mdash; it's about support, not surveillance.
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="link-child-section">
            <h3>Link a Child</h3>
            <p className="link-child-description">
              Enter your child's email to request access to their learning progress. They'll need to approve the request from their account.
            </p>
            <form onSubmit={handleLinkChild}>
              <input
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                placeholder="Child's email address"
                disabled={isLinking}
              />
              <button type="submit" disabled={isLinking || !linkEmail}>
                {isLinking ? 'Sending...' : 'Send Request'}
              </button>
            </form>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}
          </div>
        </aside>

        {/* Main content area */}
        <main className="child-details">
          {!selectedChild ? (
            <div className="no-selection">
              <h2>No Child Selected</h2>
              <p>Link a child account to view their progress.</p>
            </div>
          ) : isLoadingDetails ? (
            <div className="loading-details">
              <div className="loading-spinner" />
              <p>Loading progress...</p>
            </div>
          ) : childDetails ? (
            <>
              <div className="child-header">
                <h2>{childDetails.student.name}'s Progress</h2>
                <p className="child-email">
                  {childDetails.student.email}
                  {selectedCurriculum && (
                    <span className="child-curriculum">
                      {' '}&middot;{' '}
                      {children.find(c => c.student_id === selectedChild)?.curriculum_display_name || selectedCurriculum}
                    </span>
                  )}
                </p>
              </div>

              {/* Tab Navigation */}
              <div className="tab-navigation">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="tab-content">
                {renderTabContent()}
              </div>
            </>
          ) : null}
        </main>
      </div>

      {/* Modals */}
      {showGoalsModal && selectedChild && (
        <SetGoalsModal
          studentId={selectedChild}
          childName={selectedChildName}
          onClose={() => setShowGoalsModal(false)}
          onSaved={() => {}}
        />
      )}

      {showAssignmentModal && selectedChild && (
        <CreateAssignmentModal
          studentId={selectedChild}
          childName={selectedChildName}
          onClose={() => setShowAssignmentModal(false)}
          onCreated={() => setAssignmentRefresh((n) => n + 1)}
        />
      )}
    </div>
  );
};
